import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchProduct } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { useStore } from "@/lib/store";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Wand2, Layers } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

const ENGRAVING_FEE = 10.0; // CAD per unit — user-configurable premium add-on

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { format, addToCart, country } = useStore();
  const { isAuthenticated } = useAuth();
  const [product, setProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [exploded, setExploded] = useState(false);
  const [engraving, setEngraving] = useState("");
  const [engravingPhoto, setEngravingPhoto] = useState(null); // { name, data_url }
  const [subscribe, setSubscribe] = useState(false);
  const [activeMediaIdx, setActiveMediaIdx] = useState(0);
  const prm = usePrefersReducedMotion();

  const hasEngraving = Boolean((engraving && engraving.trim()) || engravingPhoto);

  const handleAdd = async () => {
    // Encode engraving as text; if a photo was uploaded, prepend a marker so
    // the cart line preserves that info until backend supports file uploads.
    const engravingPayload = hasEngraving
      ? (engraving && engraving.trim()) || (engravingPhoto ? `[Photo] ${engravingPhoto.name}` : null)
      : null;
    await addToCart(product.id, currentSize.label, 1, engravingPayload);
    if (subscribe) {
      if (!isAuthenticated) {
        toast("Sign in to activate Subscribe & Save", { description: "Your item was added to cart. Log in to enable auto-refills.", duration: 4200 });
      } else {
        try {
          await api.post("/account/subscriptions", {
            product_id: product.id,
            size_label: currentSize.label,
            frequency_days: 90,
          });
          toast.success("Subscription activated — refills every 90 days at 15% off");
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[pdp] subscription creation failed", err?.response?.status);
          toast.error("Could not create subscription");
        }
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    fetchProduct(slug, { country })
      .then((p) => {
        if (cancelled) return;
        setProduct(p);
        const primary = p.sizes.find((s) => !/sample/i.test(s.label)) || p.sizes[0];
        setSelectedSize(primary.label);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[pdp] fetchProduct failed", err?.response?.status);
        if (!cancelled) navigate("/shop");
      });
    return () => { cancelled = true; };
  }, [slug, navigate, country]);

  if (!product) {
    return <div data-testid="pdp-loading" className="max-w-[1280px] mx-auto px-6 py-24">
      <div className="h-[400px] sd-skeleton rounded-2xl" />
    </div>;
  }

  const currentSize = product.sizes.find((s) => s.label === selectedSize) || product.sizes[0];

  const sillageMap = { subtle: 33, moderate: 66, powerful: 100 };
  const longevityPct = Math.min(100, (product.longevity_hours / 12) * 100);

  return (
    <main data-testid="pdp-page" className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-6 sm:py-8 lg:py-14">
      <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-start">
        {/* ---- Product image ---- */}
        <div className="relative">
          <div className="relative h-[380px] sm:h-[520px] lg:h-[640px] rounded-2xl sm:rounded-3xl overflow-hidden border border-black/10 bg-gradient-to-br from-[#FAF8F5] to-[#F1ECE4] flex items-center justify-center">
            <div className="absolute inset-6 sd-amber-glow rounded-full opacity-40 pointer-events-none" />

            {/* Watermark logo — subtle on ivory */}
            <img
              src="/scentodore-logo.webp"
              alt=""
              aria-hidden
              className="absolute w-[55%] max-w-[320px] opacity-[0.05] pointer-events-none select-none"
              draggable={false}
            />

            {/* Bottle — actual product image (no blend/mask so full colors show).
                The blend is only applied to the dark cinematic hero fallback. */}
            {(() => {
              const productImg = (product.images && product.images[activeMediaIdx]) || product.image_url;
              const usingRealPhoto = Boolean(productImg);
              const src = productImg || "/scentodore-hero-bottle.webp";
              return (
                <img
                  src={src}
                  alt={product.name}
                  data-testid="pdp-bottle-image"
                  className={`relative z-10 h-full w-auto object-contain max-w-[92%] ${
                    usingRealPhoto ? "" : "sd-hero-blend"
                  } ${prm ? "" : "sd-hero-breathe"}`}
                  draggable={false}
                />
              );
            })()}

            {/* Engraving preview overlay */}
            {engraving && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-black/50 backdrop-blur-sm text-[11px] uppercase tracking-[0.24em] text-[#D4B278] border border-[#D4B278]/25 z-20">
                {engraving}
              </div>
            )}

            <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] text-[#1A1918]/55 pointer-events-none z-10">
              {product.concentration || "Extrait"} · Hand-poured
            </div>
          </div>
          {/* Media thumbnails (only shown if Shopify product has multiple images) */}
          {(product.images && product.images.length > 1) && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="pdp-media-thumbs">
              {product.images.slice(0, 6).map((url, i) => (
                <button
                  key={`thumb-${i}`}
                  onClick={() => setActiveMediaIdx(i)}
                  data-testid={`pdp-media-thumb-${i}`}
                  aria-label={`View image ${i + 1}`}
                  className={`h-16 w-14 rounded-lg overflow-hidden border-2 transition-all ${
                    i === activeMediaIdx
                      ? "border-[#C5A059] ring-2 ring-[#C5A059]/20"
                      : "border-black/10 hover:border-[#C5A059]/40 opacity-70 hover:opacity-100"
                  }`}
                  style={{ backgroundImage: `url(${url})`, backgroundSize: "cover", backgroundPosition: "center" }}
                />
              ))}
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button data-testid="pdp-exploded-toggle" variant="outline" className="rounded-full text-xs uppercase tracking-[0.14em]" onClick={() => setExploded((v) => !v)}>
              <Layers className="h-3.5 w-3.5 mr-1.5" /> {exploded ? "Hide Notes" : "Show Notes"}
            </Button>
            <Button data-testid="pdp-ar-button" variant="ghost" className="rounded-full text-xs uppercase tracking-[0.14em]">
              <Sparkles className="h-3.5 w-3.5 mr-1.5" /> View on Vanity (AR)
            </Button>
          </div>
        </div>

        {/* ---- Details ---- */}
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-2 sm:mb-3">{product.number}</div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">{product.name}</h1>
          <p className="mt-3 sm:mt-4 text-sm sm:text-base text-[#1A1918]/75 leading-relaxed">{product.tagline}</p>

          <div className="mt-5 flex flex-wrap gap-2">
            <Badge className="bg-[#2C3E35]/10 text-[#2C3E35] border border-[#2C3E35]/25 rounded-full px-3 py-1 text-[11px] tracking-[0.14em] uppercase">
              Inspired by {product.inspired_by}
            </Badge>
            <Badge className="bg-black/70 text-white/90 border-none rounded-full px-3 py-1 text-[11px] tracking-[0.14em] uppercase">
              {product.concentration} • {product.concentration_pct}% Oil
            </Badge>
            <Badge className="bg-[#C5A059]/15 text-[#1A1918] border border-[#C5A059]/40 rounded-full px-3 py-1 text-[11px] tracking-[0.14em] uppercase">
              {product.gender}
            </Badge>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-[#1A1918]/60">Longevity</div>
              <div className="mt-2"><Progress value={longevityPct} className="h-1.5 bg-[#F1ECE4] [&_[data-slot=progress-indicator]]:bg-[#C5A059]" data-testid="pdp-longevity-bar" /></div>
              <div className="text-sm mt-1">{product.longevity_hours}+ hours</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-[#1A1918]/60">Sillage</div>
              <div className="mt-2"><Progress value={sillageMap[product.sillage] || 66} className="h-1.5 bg-[#F1ECE4] [&_[data-slot=progress-indicator]]:bg-[#C5A059]" data-testid="pdp-sillage-bar" /></div>
              <div className="text-sm mt-1 capitalize">{product.sillage}</div>
            </div>
          </div>

          <div className="mt-8">
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#1A1918]/60 mb-3">Scent Pyramid</div>
            {["top", "heart", "base"].map((k) => (
              <div key={k} className="flex items-baseline gap-4 py-1.5">
                <div className="w-16 text-[11px] uppercase tracking-[0.14em] text-[#1A1918]/60">{k}</div>
                <div className="text-sm text-[#1A1918]/85">{product.notes[k].join(" · ")}</div>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-[11px] uppercase tracking-[0.16em] text-[#1A1918]/60">
                Custom Engraving <span className="text-[#C5A059]">— optional add-on</span>
              </div>
              <div className="text-[11px] font-medium text-[#C5A059] uppercase tracking-[0.14em]">
                +{format(ENGRAVING_FEE)}
              </div>
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="pdp-engraving-input"
                value={engraving}
                maxLength={22}
                onChange={(e) => setEngraving(e.target.value)}
                placeholder="Text (up to 22 characters)"
                className="bg-[#F1ECE4] border-black/10 rounded-xl"
              />
              <label
                data-testid="pdp-engraving-photo-label"
                className="cursor-pointer inline-flex items-center justify-center rounded-xl border border-black/10 bg-[#FAF8F5] hover:border-[#C5A059] px-4 text-xs uppercase tracking-[0.14em] transition-colors"
                title="Upload a photo/logo to be engraved"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Photo
                <input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  data-testid="pdp-engraving-photo-input"
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0];
                    if (!f) return;
                    if (f.size > 4 * 1024 * 1024) {
                      toast.error("Photo must be under 4 MB");
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => setEngravingPhoto({ name: f.name, data_url: reader.result });
                    reader.readAsDataURL(f);
                  }}
                />
              </label>
              {hasEngraving && (
                <Button
                  variant="outline"
                  onClick={() => { setEngraving(""); setEngravingPhoto(null); }}
                  data-testid="pdp-engraving-clear"
                  className="rounded-xl"
                >
                  Clear
                </Button>
              )}
            </div>
            {engravingPhoto && (
              <div className="mt-2 flex items-center gap-3" data-testid="pdp-engraving-photo-preview">
                <img src={engravingPhoto.data_url} alt="Engraving preview" className="h-14 w-14 rounded-lg object-cover border border-black/10" />
                <div className="text-xs text-[#1A1918]/75">
                  <div className="font-medium truncate max-w-[220px]">{engravingPhoto.name}</div>
                  <div className="text-[#1A1918]/50">Will be laser-engraved on the bottle base</div>
                </div>
              </div>
            )}
            <div className="mt-2 text-xs text-[#1A1918]/60">
              {hasEngraving
                ? `A ${format(ENGRAVING_FEE)} engraving fee will be added to this bottle.`
                : "Add a name, initials, quote, or upload a small photo/logo (JPG/PNG under 4 MB)."}
            </div>
          </div>

          <div className="mt-8">
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#1A1918]/60 mb-3">Choose Size</div>
            <div className="flex flex-wrap gap-2">
              {product.sizes.map((s) => {
                const isSelected = selectedSize === s.label;
                const isPureOil = /pure oil/i.test(s.label);
                const isSample = /sample/i.test(s.label);
                return (
                  <button
                    key={s.label}
                    data-testid={`pdp-size-${s.label.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                    onClick={() => setSelectedSize(s.label)}
                    className={`relative px-4 py-2.5 rounded-xl border text-sm transition-colors text-left min-w-[112px] ${
                      isSelected
                        ? isPureOil
                          ? "bg-gradient-to-br from-[#3B2A1D] to-[#1A1918] text-[#FAF8F5] border-[#C5A059]"
                          : "bg-[#1A1918] text-[#FAF8F5] border-[#1A1918]"
                        : isPureOil
                          ? "bg-[#FAF8F5] border-[#C5A059]/50 hover:border-[#C5A059]"
                          : "bg-[#FAF8F5] border-black/15 hover:border-[#C5A059]/40"
                    }`}
                  >
                    {isPureOil && (
                      <span
                        aria-hidden
                        className={`absolute -top-2 right-2 text-[8px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full font-medium ${
                          isSelected
                            ? "bg-[#C5A059] text-[#0B0A08]"
                            : "bg-[#C5A059] text-[#FAF8F5]"
                        }`}
                      >
                        Premium
                      </span>
                    )}
                    <div className="flex items-center gap-1.5">
                      <span>{s.label}</span>
                      {isPureOil && <Sparkles className={`h-3 w-3 ${isSelected ? "text-[#C5A059]" : "text-[#C5A059]"}`} />}
                    </div>
                    <div className={`text-[11px] mt-0.5 ${isSelected ? "text-[#FAF8F5]/75" : "text-[#1A1918]/60"}`}>{format(s.price_usd)}</div>
                    {!isSelected && isSample && (
                      <div className="text-[9px] uppercase tracking-[0.16em] mt-0.5 text-[#1A1918]/45">Try before</div>
                    )}
                    {!isSelected && isPureOil && (
                      <div className="text-[9px] uppercase tracking-[0.16em] mt-0.5 text-[#C5A059]">Roll-On · 100% Oil</div>
                    )}
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-[11px] text-[#1A1918]/55 max-w-md leading-relaxed">
              <span className="text-[#C5A059] uppercase tracking-[0.14em]">Pure Oil</span> — undiluted attar in a discreet 10ml roll-on. Wears closer to the skin with extraordinary depth and longevity beyond 14 hours.
            </p>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid="pdp-subscribe-toggle"
                checked={subscribe}
                onChange={(e) => setSubscribe(e.target.checked)}
                className="accent-[#C5A059] h-4 w-4"
              />
              Subscribe & save 15% (refills every 90 days)
            </label>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="pdp-add-to-cart-button"
              onClick={handleAdd}
              className="flex-1 bg-[#C5A059] text-[#1A1918] hover:bg-[#C5A059]/90 rounded-xl py-6 text-sm uppercase tracking-[0.18em] shadow-[0_10px_30px_rgba(197,160,89,0.24)]"
            >
              Add to Cart — {format(
                currentSize.price_usd * (subscribe ? 0.85 : 1) + (hasEngraving ? ENGRAVING_FEE : 0)
              )}
            </Button>
          </div>

          <div className="mt-10 text-sm text-[#1A1918]/85 leading-relaxed">
            {product.story}
          </div>

          <div className="mt-8">
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#1A1918]/60 mb-2">Key Ingredients</div>
            <div className="flex flex-wrap gap-1.5">
              {product.ingredients.map((i) => (
                <span key={i} className="text-[11px] px-2.5 py-1 rounded-full border border-black/10 bg-[#FAF8F5] text-[#1A1918]/75">{i}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
